import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { withSiteMeta } from '../../../../../../../shared/frontend/HOCS/withSiteMeta';
import { Link } from '../../../../../../../shared/frontend/components/link';
import { SimpleTooltip } from '../../../../../../../shared/frontend/components/presentational/tooltip';
import { asset } from '../../../../../../../shared/frontend/services/assetResolverService';
import { trackLinkingToVideos } from '../../../../../../../shared/frontend/libs/utils';
import { TVideoTrackType } from '../../../../../../../shared/common/videoUtils';
import { getPathToRun, namedRoutes, resolveRoutePath } from '../../../bundles/routes';
import { getSlugRegionBySubRegion } from '../../../shared/specific-realms/utils';
import { GuildRank } from '../guildRank';
import { TKeystoneRunRankingsTableProps } from './types';
import { TimeStyling } from './TimeStyling';
import { KeystoneRunTimeInfo } from './KeystoneRunTimeInfo';
import { KeystoneGroupMember } from './KeystoneGroupMember';

export const KeystoneRunRankingsTableFrontpage = withSiteMeta((props: TKeystoneRunRankingsTableProps) => {
  const cols = props.columns || {
    rank: true,
    dungeon: !props.dungeon.name,
    level: true,
    time: false,
    affixes: false,
    tank: true,
    healer: true,
    dps: true,
    score: false,
    video: true,
    faction: false
  };

  const hasAnyVideos = !!props.groups.find((g: any) => g.run.videos?.length > 0);
  if (!hasAnyVideos) {
    cols.video = false;
  }

  const numColumns = 1 + Object.values(cols).filter((v) => v === true).length;
  const { allSubRegions } = props;

  return (
    <table
      className={cx(
        'slds-table',
        'slds-table--bordered',
        'slds-table--striped',
        'slds-table--no-cell-buffer',
        'rio-rankings-table'
      )}
    >
      <thead>
        <tr>
          {props.title && (
            <th colSpan={numColumns} className="slds-text-align--center text-white slds-text-heading--small">
              {props.title}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {props.groups.length === 0 && (
          <tr>
            <td colSpan={3}>
              <div
                className="slds-p-around--large slds-text-align--center slds-align--absolute-center slds-text-heading--medium"
                style={{ minHeight: '250px', whiteSpace: 'normal' }}
              >
                {t('common:errors.empty-run-leaderboard')}
              </div>
            </td>
          </tr>
        )}

        {props.groups.map((group: any) => (
          <tr key={group.run.keystone_run_id} className={`faction--${group.run.faction}`}>
            {cols.rank && (
              <td data-label="Rank" className="rio-takeover-hidden slds-text-align--center">
                <Link to={getPathToRun(group.run)}>
                  <GuildRank rank={group.rank} inheritSize={true} />
                </Link>
              </td>
            )}
            <td style={{ width: 1 }}>
              <SimpleTooltip
                placement="top"
                tooltip={
                  <div>
                    {t(`raids:${group.run.dungeon.slug}.name`)}
                    <TimeStyling
                      timeMs={group.run.clear_time_ms}
                      precision={cols.timePrecision}
                      includeHours={!cols.timeSkipHours}
                    />
                    <KeystoneRunTimeInfo run={group.run} />
                  </div>
                }
              >
                <div>
                  <span className="slds-avatar slds-avatar--x-small slds-m-right--x-small rio-border--light">
                    <img
                      loading="lazy"
                      src={asset(`/images/keystone-icons/${group.run.dungeon.id}.jpg`)}
                      alt={t(`raids:${group.run.dungeon.slug}.name`)}
                      style={{ width: 18, height: 18 }}
                    />
                  </span>
                  <Link to={getPathToRun(group.run)} className="rio-realm-link">
                    <span>+{group.run.mythic_level}</span>
                    <span style={{ fontSize: 6, position: 'relative', left: 4, top: '-0.3rem' }}>
                      {_.range(group.run.num_chests).map((n) => (
                        <i key={n} className="rio-spaced-element--small fa fa-star text-gold" />
                      ))}
                    </span>
                  </Link>
                </div>
              </SimpleTooltip>
            </td>

            {(cols.tank || cols.healer || cols.dps) && (
              <td colSpan={3} className="slds-cell-wrap">
                <KeystoneGroupMember
                  teamDetailsRoute={props.teamDetailsRoute}
                  team={group.run.keystone_team_id}
                  season={group.run.season}
                  showRoleIcon={cols.roleIcons !== false}
                  {...(group.run.roster.length >= 1 ? group.run.roster[0] : {})}
                />{' '}
                <KeystoneGroupMember
                  teamDetailsRoute={props.teamDetailsRoute}
                  team={group.run.keystone_team_id}
                  season={group.run.season}
                  showRoleIcon={cols.roleIcons !== false}
                  {...(group.run.roster.length >= 2 ? group.run.roster[1] : {})}
                />{' '}
                <KeystoneGroupMember
                  teamDetailsRoute={props.teamDetailsRoute}
                  team={group.run.keystone_team_id}
                  season={group.run.season}
                  showRoleIcon={cols.roleIcons !== false}
                  {...(group.run.roster.length >= 3 ? group.run.roster[2] : {})}
                />{' '}
                <KeystoneGroupMember
                  teamDetailsRoute={props.teamDetailsRoute}
                  team={group.run.keystone_team_id}
                  season={group.run.season}
                  {...(group.run.roster.length >= 4 ? group.run.roster[3] : {})}
                />{' '}
                <KeystoneGroupMember
                  teamDetailsRoute={props.teamDetailsRoute}
                  team={group.run.keystone_team_id}
                  season={group.run.season}
                  {...(group.run.roster.length >= 5 ? group.run.roster[4] : {})}
                />
              </td>
            )}
            {cols.video &&
              !props.siteConfig.isEmbedded &&
              (group.run.videos && group.run.videos.length > 0 ? (
                <td className="slds-cell-wrap">
                  <Link
                    className="no-underline-hover"
                    to={resolveRoutePath(namedRoutes.MythicKeystoneRun, {
                      season: group.run.season,
                      id: group.run.keystone_run_id
                    })}
                    onClick={() => trackLinkingToVideos(TVideoTrackType.MPlus)}
                  >
                    <span className="slds-grid slds-grid--align-center text-white">
                      <i className="fa-solid fa-video-camera fa-lg rio-hover-color-raiderio"></i>
                    </span>
                  </Link>
                </td>
              ) : (
                <td></td>
              ))}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={6} className="slds-text-align--center slds-text-body--small">
            <Link
              className="rio-realm-link--muted"
              to={resolveRoutePath(
                props.affixes ? namedRoutes.KeystoneRunAffixRankings : namedRoutes.KeystoneRunRankings,
                {
                  season: props.season,
                  dungeon: 'all',
                  region: getSlugRegionBySubRegion(props.region?.slug, allSubRegions) || 'world',
                  type: 'leaderboards-strict',
                  affixes: props.affixes
                }
              )}
            >
              {t('common:leaderboard.view-leaderboard-prompt')}
            </Link>
          </td>
        </tr>
      </tfoot>
    </table>
  );
});
